/**
 * Sweden's trains where they really are, from Trafikverket's train
 * positions (`/rt/trains`, which asks smartmaps' tile store: see
 * realtime/trains.ts). Each comes with where it was last seen, which way it
 * was heading and how fast, and what it is and where it is going.
 *
 * A position is from a few seconds to a minute old, so where the train is
 * now is reckoned on from it at its speed (`reckon`), and the game's train
 * is driven to be there (`paceTo`): a little faster when behind, slower
 * when ahead, never jumping.
 *
 * Pure: `app/trains.ts` fetches, `three/trains.ts` drives.
 */

export interface LiveTrain {
  /** The advertised train number: "3527". */
  number: string;
  lat: number;
  lon: number;
  /** Heading, degrees clockwise from north; undefined if not given. */
  bearing: number | undefined;
  /** m/s; undefined where Trafikverket does not say (not standing: not known). */
  speed: number | undefined;
  /** When it was at (lat, lon), ms since the epoch. */
  time: number;
  /** "Västtågen", "SJ Snabbtåg"; '' for a freight train. */
  product: string;
  operator: string;
  from: string;
  to: string;
}

/** The Worker's answer as `LiveTrain`s: anything without a place or a time left out. */
export function readTrains(body: unknown): LiveTrain[] {
  const list = (body as { trains?: unknown[] } | null)?.trains;
  if (!Array.isArray(list)) return [];
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return list.flatMap((raw): LiveTrain[] => {
    const t = raw as Record<string, unknown>;
    const lat = Number(t.lat), lon = Number(t.lon), time = Date.parse(str(t.time));
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || !Number.isFinite(time) || str(t.number) === '') return [];
    const bearing = t.bearing === null || t.bearing === undefined ? undefined : Number(t.bearing);
    const kmh = t.speed === null || t.speed === undefined ? NaN : Number(t.speed);
    return [{
      number: str(t.number), lat, lon,
      bearing: bearing !== undefined && Number.isFinite(bearing) ? bearing : undefined,
      speed: Number.isFinite(kmh) ? Math.max(0, kmh) / 3.6 : undefined,
      time, product: str(t.product), operator: str(t.operator), from: str(t.from), to: str(t.to),
    }];
  });
}

/** The scene's direction (x east, z south) of a compass bearing. */
export function bearingDirection(bearing: number): { dx: number; dz: number } {
  const b = (bearing * Math.PI) / 180;
  return { dx: Math.sin(b), dz: -Math.cos(b) };
}

/**
 * How fast a train went between two of its reports, m/s, for one whose
 * speed is not given: undefined if they are the same report, or too close
 * in time to tell.
 */
export function speedBetween(a: { lat: number; lon: number; time: number }, b: { lat: number; lon: number; time: number }): number | undefined {
  const seconds = (b.time - a.time) / 1000;
  if (seconds < 3) return undefined;
  const north = (b.lat - a.lat) * 111_320, east = (b.lon - a.lon) * 111_320 * Math.cos((a.lat * Math.PI) / 180);
  return Math.min(70, Math.hypot(north, east) / seconds);
}

/** Longest a position is reckoned on from, seconds: past this the train may well have stopped. */
export const RECKON_MOST = 90;

/** How far a train seen `t` ms ago going `speed` m/s has gone since, metres, as far as can be guessed. */
export function reckon(speed: number, seenAt: number, now: number): number {
  return speed * Math.max(0, Math.min(RECKON_MOST, (now - seenAt) / 1000));
}

/**
 * The speed to drive at, m/s, to come up with where the real train is:
 * `ahead` metres ahead of the game's along the track (negative, behind),
 * the real one going `speed`. Closes most of the gap in about half a
 * minute, and never runs backwards.
 */
export function paceTo(speed: number, ahead: number, top: number): number {
  const chase = Math.max(-8, Math.min(12, ahead / 25));
  return Math.max(0, Math.min(top * 1.15, speed + chase));
}
